import React, { useState } from 'react';
import { X, Check, Loader2, Trash2 } from 'lucide-react';
import { transactionAPI } from '../api/client';
import { useAnalytics } from '../hooks/useAnalytics';
import { CATEGORIES } from '../constants/categories';
import { useTokens } from '../context/ThemeContext';

const SUPPORTED_CURRENCIES = [
  { code: 'INR', label: 'Indian Rupee (₹)' },
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'GBP', label: 'British Pound (£)' },
  { code: 'JPY', label: 'Japanese Yen (¥)' },
  { code: 'AUD', label: 'Australian Dollar (A$)' },
  { code: 'CAD', label: 'Canadian Dollar (C$)' },
  { code: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'SGD', label: 'Singapore Dollar (S$)' },
  { code: 'AED', label: 'UAE Dirham (AED)' },
  { code: 'SAR', label: 'Saudi Riyal (SAR)' },
  { code: 'MYR', label: 'Malaysian Ringgit (MYR)' },
  { code: 'THB', label: 'Thai Baht (THB)' },
  { code: 'IDR', label: 'Indonesian Rupiah (IDR)' },
  { code: 'PHP', label: 'Philippine Peso (₱)' },
  { code: 'KRW', label: 'South Korean Won (₩)' },
  { code: 'CNY', label: 'Chinese Yuan (¥)' },
  { code: 'HKD', label: 'Hong Kong Dollar (HK$)' },
  { code: 'VND', label: 'Vietnamese Dong (₫)' },
  { code: 'TWD', label: 'Taiwan Dollar (NT$)' },
  { code: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { code: 'MXN', label: 'Mexican Peso (MX$)' },
  { code: 'BRL', label: 'Brazilian Real (R$)' },
  { code: 'ZAR', label: 'South African Rand (R)' },
  { code: 'NOK', label: 'Norwegian Krone (kr)' },
  { code: 'SEK', label: 'Swedish Krona (kr)' },
  { code: 'DKK', label: 'Danish Krone (kr)' },
  { code: 'NGN', label: 'Nigerian Naira (₦)' },
  { code: 'KES', label: 'Kenyan Shilling (KSh)' },
  { code: 'EGP', label: 'Egyptian Pound (E£)' },
  { code: 'PKR', label: 'Pakistani Rupee (₨)' },
  { code: 'BDT', label: 'Bangladeshi Taka (৳)' },
];

function TransactionEditModal({ transaction, onClose, onSaved, onDeleted, customCategories = [] }) {
  const t = useTokens();
  const { track } = useAnalytics();
  const [name, setName] = useState(transaction.receiver || transaction.merchant || transaction.description || '');
  const initialCategory = transaction.category || 'Other';
  const [category, setCategory] = useState(initialCategory);
  const [date, setDate] = useState(() => {
    const d = transaction.transactionDate ? new Date(transaction.transactionDate) : new Date();
    return d.toISOString().slice(0, 10);
  });
  const [amount, setAmount] = useState(String(transaction.amount || ''));
  const [currency, setCurrency] = useState(transaction.currency || 'INR');
  const [rememberCategory, setRememberCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [updatedCount, setUpdatedCount] = useState(null);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this transaction? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await transactionAPI.delete(transaction._id || transaction.id);
      if (onDeleted) onDeleted(transaction._id || transaction.id);
      onClose();
    } catch (err) {
      setError('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name cannot be empty.');
      return;
    }
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Amount must be a positive number.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await transactionAPI.update(transaction._id || transaction.id, {
        receiver: name.trim(),
        description: name.trim(),
        category,
        transactionDate: date,
        amount: parsedAmount,
        currency,
        rememberCategory,
      });
      track('transaction_edited', { category });
      if (rememberCategory && res.data.updatedCount > 0) {
        setUpdatedCount(res.data.updatedCount);
        onSaved(res.data.transaction);
        setSaving(false);
        return;
      }
      onSaved(res.data.transaction);
      onClose();
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const catBtn = { padding: '6px 12px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', backgroundColor: t.chipBg, color: t.chipText };
  const catBtnActive = { backgroundColor: t.itemSelected, color: t.itemSelectedText };
  const catBtnCustom = { backgroundColor: '#ede9fe', color: '#5b21b6' };
  const spin = { animation: 'spin 1s linear infinite' };
  const inputStyle = { padding: '10px 14px', border: `1px solid ${t.inputBorder}`, borderRadius: '10px', fontSize: '14px', color: t.text, outline: 'none', boxSizing: 'border-box', backgroundColor: t.inputBg };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', zIndex: 1100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px' }} onClick={handleOverlayClick}>
      <div style={{ backgroundColor: t.surface, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${t.borderLight}` }}>
          <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: t.text }}>Edit transaction</h2>
          <button onClick={onClose} style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '50%' }}>
            <X size={20} color={t.textSecondary} />
          </button>
        </div>

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: t.textSlate }}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
              autoFocus
              placeholder="Transaction name"
            />
          </div>

          {/* Amount + Currency */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: t.textSlate }}>Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="any"
                style={{ ...inputStyle, width: '100%' }}
                placeholder="0.00"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '140px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: t.textSlate }}>Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}
              >
                {SUPPORTED_CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: t.textSlate }}>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>

          {/* Category */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: t.textSlate }}>Category</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {CATEGORIES.map((cat) => (
                <button key={cat} type="button" onClick={() => { setCategory(cat); if (cat === initialCategory) setRememberCategory(false); }} style={{ ...catBtn, ...(category === cat ? catBtnActive : {}) }}>
                  {cat}
                </button>
              ))}
              {customCategories.map((cat) => (
                <button key={cat} type="button" onClick={() => { setCategory(cat); if (cat === initialCategory) setRememberCategory(false); }} style={{ ...catBtn, ...catBtnCustom, ...(category === cat ? catBtnActive : {}) }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Remember categorisation — only shown when category has changed */}
          {category !== initialCategory && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={rememberCategory}
                  onChange={(e) => setRememberCategory(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: t.textSlate }}
                />
                <span style={{ fontSize: '13px', color: t.textSecondary }}>Remember for future transactions from this merchant</span>
              </label>
              {rememberCategory && (
                <span style={{ fontSize: '12px', color: t.textMuted, paddingLeft: '26px' }}>
                  All existing transactions from this merchant will also be updated to <strong>{category}</strong>.
                </span>
              )}
            </div>
          )}

          {/* Confirmation after bulk update */}
          {updatedCount !== null && (
            <div style={{ fontSize: '13px', color: t.success || '#16a34a', backgroundColor: t.successBg || '#f0fdf4', padding: '10px 14px', borderRadius: '10px' }}>
              Updated {updatedCount} other transaction{updatedCount !== 1 ? 's' : ''} to <strong>{category}</strong>.
              <button onClick={onClose} style={{ marginLeft: '10px', fontSize: '13px', color: t.textSlate, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Close</button>
            </div>
          )}

          {error && <div style={{ fontSize: '13px', color: t.danger }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
            <button onClick={handleDelete} disabled={deleting} style={{ padding: '11px 14px', borderRadius: '10px', border: 'none', backgroundColor: t.dangerBg, color: t.danger, fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexShrink: 0 }} title="Delete transaction">
              {deleting ? <Loader2 size={15} style={spin} /> : <Trash2 size={15} />}
            </button>
            <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', backgroundColor: t.chipBg, color: t.chipText, fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', backgroundColor: t.textSlate, color: 'white', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              {saving ? <Loader2 size={15} style={spin} /> : <Check size={15} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TransactionEditModal;
